import { Injectable} from '@angular/core';
import { ApiBaseService } from 'libs/shared-services/src/lib/api-base.service';
import { ToasterService } from 'libs/shared-services/src/lib/toaster.service';
import { BehaviorSubject, Observable, Subscription, catchError, map, of } from 'rxjs';
import { OrderListResponse, OrderResponse } from 'libs/shared-models/src/lib/order/order-response';

import { RestaurantOrdersStateService } from './data-store/restaurant-orders.state';
import { environment } from 'apps/restaurant/src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class RestaurantOrdersService {

    private isLoadingOrdersFromAPI$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private selectedOrderRequest: SelectedOrderRequest | null = null;

    // TODO: maybe add loading state for this one too (restaurant init services)
    constructor(
        private apiService: ApiBaseService,
        private toasterService: ToasterService,
        private ordersStateService: RestaurantOrdersStateService
    ) {
    }

    public fetchOrdersListAPI() {
        this.isLoadingOrdersFromAPI$.next(true);
        this.fetchOrdersListAPI$().subscribe((res) => {
            if (res) {
                this.ordersStateService.setData(res.results);

                // (re)select the opened order
                this.refreshCurrentSelectedOrder();
            }
            this.isLoadingOrdersFromAPI$.next(false);
        })
    }

    public isLoadingOrdersAPI$(): Observable<boolean> {
        return this.isLoadingOrdersFromAPI$.asObservable();
    }

    /*
        Fetch all orders list (for the Orders section)
    */
    private fetchOrdersListAPI$(): Observable<OrderListResponse> {
        const url = environment.API_GET_ORDERS_LIST + "?pageSize=100";
        const apiCall = this.apiService.get(url);
        return apiCall.pipe(
            map((res) => {
                const ordersRes = Object.assign(new OrderListResponse(), res);                
                return ordersRes;
            }),
            catchError((err: any, caught: Observable<any>): Observable<any> => {
                this.toasterService.showError("Error", err?.error?.message);
                return of({});
            })
       );
    }

    public ordersList$(): Observable<OrderResponse[]> {
        return this.ordersStateService.getData$();
    }

    /*
        Fetch individual order (for the Orders section)
        This is fetched every time a order is clicked / opened. 
        We want to have it up-to-date each and every time
    */
    private fetchOrderByIdAPI$(orderId: string): Observable<OrderResponse> {
        let url = environment.API_GET_INDIVIDUAL_ORDER.replace('{orderId}', orderId);
        const apiCall = this.apiService.get(url);
        return apiCall.pipe(
            map((res) => {
                const order = Object.assign(new OrderResponse(), res);                
                return order;
            }),
            catchError((err: any, caught: Observable<any>): Observable<any> => {
                this.toasterService.showError("Error", err?.error?.message);
                return of({});
            })
       );
    }

    /*
        Used to update each individual order with new API call
        Can be used also to just update some order - or also to consider it the current selected one (isSelected)
    */
    public fetchOrderByIdAPI(orderId: string) {
        if (this.selectedOrderRequest?.orderId === orderId) {
            return;
        }

        this.selectedOrderRequest?.subscription.unsubscribe();

        const requestSubscription = new Subscription();
        this.selectedOrderRequest = {
            orderId,
            subscription: requestSubscription,
        };

        requestSubscription.add(this.fetchOrderByIdAPI$(orderId).subscribe({
          next: (order) => {
            if (this.selectedOrderRequest?.subscription !== requestSubscription) {
                return;
            }

            if (order && Object.keys(order).length > 0) {
                this.ordersStateService.replaceAndSetCurrentSelectOrder(order);
                
                setTimeout(() => {
                    const el = document.querySelector(`[data-order-id="${order.id}"]`);
                    if (el) {
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                });
            }
            else {
                // fetch it again (eg: retry 3 times?)
                // for now, do nothing else
            }
          },
          complete: () => {
            if (this.selectedOrderRequest?.subscription === requestSubscription) {
                this.selectedOrderRequest = null;
            }
          },
        }));
    }

    public getOrderyById(orderId: string): OrderResponse | undefined {
        return this.ordersStateService.getData().find(order => order.id === orderId);
    }

    public getCurrentSelectedOrder(): OrderResponse | null {
        return this.ordersStateService.getCurrentSelectedOrder();
    }

    public getCurrentSelectedOrder$(): Observable<OrderResponse | null> {
        return this.ordersStateService.getCurrentSelectedOrder$();
    }

    public setCurrentSelectedOrder(order: OrderResponse) {
        // fetch a new session of this data and then select it:    
        this.fetchOrderByIdAPI(order.id);
    }

    public refreshCurrentSelectedOrder() {
        const list = this.ordersStateService.getData();
        const alreadyPreselected = this.ordersStateService.getCurrentSelectedOrder();
        const preselectedElement = list.find((item) => item.id === alreadyPreselected?.id);

        if (!!preselectedElement) {
            this.ordersStateService.setCurrentSelectedOrder(preselectedElement); 
        } else {
            this.preselectFirstOrder();
        }
    }

    public preselectFirstOrder() {
        if (this.ordersStateService.getData()[0]) {
            this.ordersStateService.setCurrentSelectedOrder(this.ordersStateService.getData()[0]);
            return;
        }
        // default
        this.ordersStateService.setCurrentSelectedOrder(null);        
    }
}

interface SelectedOrderRequest {
    orderId: string;
    subscription: Subscription;
}
